import { ApiClassificationItem } from "@incendium/api";
import { Chip, OutlinedTextFieldProps } from "@mui/material";
import { useClassifications } from "Hooks";
import { useMemo } from "react";
import StyledMultiSelect from "Components/UI/StyledMultiSelect";

interface ITraitsMultiSelectProps
  extends Omit<OutlinedTextFieldProps, "onChange"> {
  values: ApiClassificationItem[];
  disableClearable?: boolean;
  onChange: (items?: ApiClassificationItem[]) => void;
}

function TraitsMultiSelect({
  values,
  label,
  helperText,
  variant,
  size,
  disableClearable,
  fullWidth,
  sx,
  onChange,
}: ITraitsMultiSelectProps) {
  const { classifications } = useClassifications();

  // Flatten all classification items from all classifications
  const allClassificationItems = useMemo(() => {
    const items: ApiClassificationItem[] = [];
    classifications.forEach((classification) => {
      if (classification.classificationItems) {
        items.push(...classification.classificationItems);
      }
    });
    return items;
  }, [classifications]);

  const handleChange = (newItems: ApiClassificationItem[]) => {
    if (!newItems || newItems.length === 0) {
      onChange([]);
      return;
    }

    // Group items by classification ID
    const itemsByClassification = new Map<number, ApiClassificationItem[]>();
    newItems.forEach((item) => {
      const classificationId = item.classificationId;
      if (classificationId) {
        if (!itemsByClassification.has(classificationId)) {
          itemsByClassification.set(classificationId, []);
        }
        itemsByClassification.get(classificationId)!.push(item);
      }
    });

    // For each classification, keep only the last selected item (most recently added)
    const filteredItems: ApiClassificationItem[] = [];
    itemsByClassification.forEach((items) => {
      // Keep only the last item from each classification
      filteredItems.push(items[items.length - 1]);
    });

    onChange(filteredItems);
  };

  const getOptionLabel = (o: ApiClassificationItem) => {
    const classification = classifications.find(
      (c) => c.id === o.classificationId
    );
    const classificationName = classification?.name || "Unknown";
    return `${classificationName}: ${o.value || ""}`;
  };

  return (
    <StyledMultiSelect
      sx={sx}
      size={size}
      fullWidth={fullWidth}
      multiple
      options={allClassificationItems}
      isOptionEqualToValue={(option, value) => {
        if (!option || !value) return false;
        return Number(option.id) === Number(value.id);
      }}
      value={values}
      onChange={(e, v) => {
        handleChange(v || []);
      }}
      disableClearable={disableClearable as any}
      getOptionLabel={getOptionLabel}
      groupBy={(option: ApiClassificationItem) => {
        const classification = classifications.find(
          (c) => c.id === option.classificationId
        );
        return classification?.name || "Unknown";
      }}
      renderTags={(value, getTagProps) =>
        value.map((option, index) => {
          const { key, ...tagProps } = getTagProps({ index });
          return (
            <Chip
              key={key}
              size={size}
              variant="outlined"
              color="primary"
              label={getOptionLabel(option)}
              {...tagProps}
            />
          );
        })
      }
      label={label}
      helperText={helperText}
      textFieldProps={{
        variant,
      }}
    />
  );
}

export default TraitsMultiSelect;

